import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import {
  X,
  Wallet,
  Percent,
  ChevronDown,
  ChevronUp,
  Check,
  ArrowRight,
  Loader2,
  CreditCard,
  Sparkles,
  Ticket,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useWallet } from '../hooks/useWallet';
import { walletService } from '../services/wallet';
import { WalletCoupon, CouponValidationResult } from '../types/wallet';
import { LockClosed12Filled, LockClosed16Filled } from '@fluentui/react-icons';

interface RechargeWalletProps {
  closeModal?: () => void;
}

const RechargeWallet: React.FC<RechargeWalletProps> = ({ closeModal }) => {
  const { user } = useAuth();
  const isB2B = window.location.pathname.startsWith('/b2b');
  const { balance, refetchBalance } = useWallet(user?.id || 0, isB2B);

  // Quick amounts (kept exactly as required)
  const QUICK_AMOUNTS = isB2B
    ? [1000, 2000, 5000, 10000]
    : [500, 1000, 2000, 5000];

  const minAmount = isB2B ? 1000 : 500;
  const [amount, setAmount] = useState<number>(isB2B ? 1000 : 500);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showBillSummary, setShowBillSummary] = useState(false);
  const [showAllCoupons, setShowAllCoupons] = useState(false);

  // Coupons state
  const [availableCoupons, setAvailableCoupons] = useState<WalletCoupon[]>([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] =
    useState<CouponValidationResult | null>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const usermail = user?.email;

  // Fetch eligible coupons on mount or user change
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        setIsLoadingCoupons(true);
        const coupons = await walletService.getCoupons(
          user?.id,
          isB2B ? 'B2B' : 'B2C'
        );
        setAvailableCoupons(coupons);
      } catch (err) {
        console.error('Failed to load coupons', err);
      } finally {
        setIsLoadingCoupons(false);
      }
    };
    fetchCoupons();
  }, [user?.id, isB2B]);

  // Re-validate applied coupon when amount changes
  useEffect(() => {
    if (appliedCoupon?.couponCode) {
      validateAndApplyCoupon(appliedCoupon.couponCode, false);
    }
  }, [amount]);

  const validateAndApplyCoupon = async (code: string, showToast = true) => {
    if (!code.trim()) {
      if (showToast) toast.error('Please enter a coupon code');
      return;
    }
    if (amount < minAmount) {
      if (showToast) toast.error(`Minimum recharge amount is ₹${minAmount}`);
      return;
    }

    try {
      setIsValidatingCoupon(true);
      const res = await walletService.validateCoupon({
        code: code.trim().toUpperCase(),
        amount,
        user_id: user?.id,
        type: isB2B ? 'B2B' : 'B2C',
      });

      if (res.valid) {
        setAppliedCoupon(res);
        setManualCode('');
        setShowAllCoupons(false);
        if (showToast) {
          toast.success(
            `Coupon "${res.couponCode}" applied! ₹${res.bonusAmount} extra bonus credited.`
          );
        }
      } else {
        if (appliedCoupon?.couponCode === code) {
          setAppliedCoupon(null);
        }
        if (showToast) {
          toast.error(res.message || 'Invalid coupon code');
        }
      }
    } catch (err: any) {
      if (showToast) {
        toast.error(err?.response?.data?.message || 'Error validating coupon');
      }
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    toast.success('Coupon removed');
  };

  const handlePay = async () => {
    if (isB2B && amount < 1000) {
      toast.error('Minimum recharge amount for B2B is ₹1,000');
      return;
    }
    if (!isB2B && amount < 500) {
      toast.error('Minimum recharge amount for B2C is ₹500');
      return;
    }

    try {
      setIsProcessing(true);

      // 🔹 B2B -> Cashfree Payments
      if (isB2B) {
        const fullName =
          `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
        const mobile =
          (user as any)?.mobileNo ||
          (user as any)?.mobile ||
          (user as any)?.phone ||
          '';

        const userId =
          user?.id || (user as any)?.userId || localStorage.getItem('USER_ID');
        if (!userId) {
          toast.error('User session not found. Please log in again.');
          return;
        }

        const payload: any = {
          orderAmount: amount,
          customerEmail: usermail || '',
          customerName: fullName,
          customerPhone: mobile,
        };
        if (appliedCoupon?.couponCode) {
          payload.couponCode = appliedCoupon.couponCode;
        }

        const res = await api.post(
          `/b2b/paymentGateway/create/${userId}/paymentLink`,
          payload
        );

        let rawLink = res.data?.paymentLink || res.data;
        let paymentLink = '';
        let paymentSessionId = '';
        let backendMessage = '';

        if (typeof rawLink === 'string') {
          if (rawLink.startsWith('http://') || rawLink.startsWith('https://')) {
            paymentLink = rawLink;
          } else {
            try {
              const parsed = JSON.parse(rawLink);
              if (parsed.payment_session_id)
                paymentSessionId = parsed.payment_session_id;
              if (parsed.payment_link) paymentLink = parsed.payment_link;
              if (parsed.message) backendMessage = parsed.message;
            } catch (e) {}
          }
        } else if (rawLink && typeof rawLink === 'object') {
          if (rawLink.payment_session_id)
            paymentSessionId = rawLink.payment_session_id;
          if (rawLink.payment_link) paymentLink = rawLink.payment_link;
          if (rawLink.message) backendMessage = rawLink.message;
        }

        if (
          paymentSessionId &&
          typeof (window as any).Cashfree === 'function'
        ) {
          if (closeModal) closeModal();
          const mode =
            process.env.REACT_APP_CASHFREE_MODE ||
            (process.env.NODE_ENV === 'production' ? 'production' : 'sandbox');
          const cashfree = (window as any).Cashfree({ mode });
          cashfree.checkout({
            paymentSessionId,
            redirectTarget: '_modal',
          });
          return;
        } else if (paymentLink || paymentSessionId) {
          const targetUrl =
            paymentLink ||
            `https://payments.cashfree.com/order/#${paymentSessionId}`;
          toast.success('Redirecting to Cashfree B2B Payment Gateway...');
          if (closeModal) closeModal();
          window.location.href = targetUrl;
          return;
        } else {
          toast.error(
            backendMessage || 'Failed to generate B2B Cashfree payment link'
          );
          return;
        }
      }

      // 🔹 B2C -> Zoho Payments
      const res = await api.post(`/createPaymentSession`, {
        amount,
        email: usermail || '',
        user_id: user?.id,
        coupon_code: appliedCoupon?.couponCode || undefined,
      });

      const data = res.data;
      const sessionId = data?.payments_session?.payments_session_id;

      if (!sessionId) {
        toast.error('Payment session creation failed');
        return;
      }

      // Cleanup any dangling widgets
      document.querySelectorAll('[id^="zp"]').forEach((el) => {
        if (
          el.id.toLowerCase().includes('widget') ||
          el.id.toLowerCase().includes('payment')
        ) {
          el.remove();
        }
      });
      document.querySelectorAll('iframe').forEach((iframe) => {
        if (iframe.src.includes('zoho') || iframe.src.includes('zpay')) {
          iframe.remove();
        }
      });

      const config = {
        account_id: '60055955116',
        domain: 'IN',
        otherOptions: {
          api_key:
            '1003.d8fe436faa08e89da33e206270ae93c7.be2849dea79729782f098e46c2bde824',
        },
      };
      const instance = new (window as any).ZPayments(config);

      const options = {
        amount: amount.toFixed(2),
        currency_code: 'INR',
        payments_session_id: sessionId,
        description: 'B2C Wallet Recharge',
        transaction_type: 'payment',
        address: {
          email: usermail,
        },
      };

      if (closeModal) {
        closeModal();
      }

      await instance.requestPaymentMethod(options);

      toast.success(
        'Payment initiated! Your wallet balance will update shortly.'
      );

      let attempts = 0;
      const pollInterval = setInterval(() => {
        refetchBalance();
        attempts++;
        if (attempts >= 5) {
          clearInterval(pollInterval);
        }
      }, 2500);
    } catch (err) {
      console.error('Payment Error:', err);
      if ((err as any)?.code !== 'widget_closed') {
        toast.error('Payment failed');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Featured coupon to display when none is applied
  const featuredCoupon =
    availableCoupons.length > 0 ? availableCoupons[0] : null;
  const totalCredited = appliedCoupon ? appliedCoupon.creditedAmount : amount;

  return (
    <div className="w-full max-w-md mx-auto rounded-3xl p-0 sm:p-2 relative overflow-hidden transition-all">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100">
        <h2 className="text-lg sm:text-xl font-bold text-slate-800">
          Recharge Your Wallet
        </h2>

        {/* <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-[#EAF5F2] border border-[#C6E7DE] px-3 py-1 rounded-full text-xs font-semibold text-[#0D6D6F]">
            <Wallet size={13} className="text-[#0D6D6F]" />
            <span>
              ₹
              {(balance ?? 0).toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div> */}
      </div>

      {/* Amount Input Section */}
      <div className="text-center py-6">
        <p className="text-[11px] font-bold text-gray-400 tracking-wider uppercase mb-2">
          Enter Amount to Add
        </p>
        <div className="inline-flex items-center justify-center">
          <span className="text-3xl sm:text-4xl font-extrabold text-slate-800 mr-1">
            ₹
          </span>
          <input
            type="number"
            value={amount === 0 ? '' : amount}
            onChange={(e) => {
              const val = e.target.value === '' ? 0 : Number(e.target.value);
              setAmount(val);
            }}
            placeholder="0"
            className="w-48 text-4xl sm:text-5xl font-black text-slate-900 bg-transparent text-center outline-none border-b-2 border-transparent focus:border-[#0D6D6F] transition-colors pb-1"
            min={minAmount}
          />
        </div>
        {amount < minAmount && amount > 0 && (
          <p className="text-xs text-amber-600 mt-2 font-medium">
            Minimum recharge amount is ₹{minAmount}
          </p>
        )}
      </div>

      {/* Quick Amounts Pills */}
      <div className="grid grid-cols-4 gap-2 mb-5">
        {QUICK_AMOUNTS.map((amt) => {
          const isSelected = amount === amt;
          return (
            <button
              key={amt}
              type="button"
              onClick={() => setAmount(amt)}
              className={`py-2 px-1 text-xs sm:text-sm font-bold rounded-xl border transition-all text-center ${
                isSelected
                  ? 'bg-[#EAF5F2] border-[#0D6D6F] text-[#0D6D6F] shadow-sm scale-[1.02]'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              +₹{amt}
            </button>
          );
        })}
      </div>

      {/* Offers & Coupons Section - only shown if coupons exist or one is applied */}
      {(availableCoupons.length > 0 || appliedCoupon) && (
        <div className="mb-5">
          <div className="bg-[#F0FAF7] border border-[#D5EFE6] rounded-2xl p-3.5 sm:p-4 transition-all">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-[#0D6D6F] text-white flex items-center justify-center shrink-0 shadow-sm">
                  <Percent size={18} strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                  {appliedCoupon ? (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-black text-[#0D6D6F] uppercase tracking-wide">
                          {appliedCoupon.couponCode}
                        </span>
                        <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-1.5 py-0.2 rounded">
                          Applied
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 truncate mt-0.5">
                        Bonus ₹{appliedCoupon.bonusAmount} will be credited
                      </p>
                    </>
                  ) : featuredCoupon ? (
                    <>
                      <p className="text-xs font-bold text-slate-800 uppercase tracking-wide truncate">
                        {featuredCoupon.code} - {featuredCoupon.title}
                      </p>
                      <p className="text-[11px] text-gray-500 truncate mt-0.5">
                        {featuredCoupon.description}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-bold text-slate-800">
                        Have a Coupon Code?
                      </p>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Apply to get extra wallet bonus
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="shrink-0">
                {appliedCoupon ? (
                  <button
                    type="button"
                    onClick={handleRemoveCoupon}
                    className="text-xs font-bold text-rose-600 hover:text-rose-700 hover:underline px-2 py-1"
                  >
                    Remove
                  </button>
                ) : featuredCoupon ? (
                  <button
                    type="button"
                    onClick={() => validateAndApplyCoupon(featuredCoupon.code)}
                    disabled={isValidatingCoupon}
                    className="text-xs font-bold text-[#0D6D6F] hover:bg-[#E0F2ED] bg-white border border-[#0D6D6F]/30 px-3 py-1.5 rounded-lg transition-colors shadow-2xs"
                  >
                    {isValidatingCoupon ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      'Apply'
                    )}
                  </button>
                ) : null}
              </div>
            </div>

            {/* Manual Input & View All Coupons Row */}
            {!appliedCoupon && (
              <div className="mt-3 pt-3 border-t border-[#D5EFE6]/70 flex flex-col sm:flex-row items-center gap-2">
                <div className="relative flex-1 w-full flex items-center">
                  <Ticket
                    size={14}
                    className="absolute left-2.5 text-gray-400"
                  />
                  <input
                    type="text"
                    value={manualCode}
                    onChange={(e) =>
                      setManualCode(e.target.value.toUpperCase())
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        validateAndApplyCoupon(manualCode);
                      }
                    }}
                    placeholder="Enter Coupon Code"
                    className="w-full pl-8 pr-16 py-1.5 text-xs font-semibold uppercase bg-white border border-gray-200 rounded-lg outline-none focus:border-[#0D6D6F] text-slate-800 placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => validateAndApplyCoupon(manualCode)}
                    disabled={isValidatingCoupon || !manualCode.trim()}
                    className="absolute right-1 text-[11px] font-bold text-[#0D6D6F] disabled:text-gray-400 px-2 py-1 hover:underline"
                  >
                    Apply
                  </button>
                </div>

                {/* Only show View All if more than 1 coupon is available */}
                {availableCoupons.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setShowAllCoupons(!showAllCoupons)}
                    className="text-xs font-bold text-[#0D6D6F] hover:underline whitespace-nowrap self-end sm:self-center"
                  >
                    {showAllCoupons
                      ? 'Hide Coupons'
                      : `View All Coupons (${availableCoupons.length}) >`}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Expandable All Coupons Drawer - only if more than 1 available */}
          {showAllCoupons && !appliedCoupon && availableCoupons.length > 1 && (
            <div className="mt-2 bg-white border border-gray-200 rounded-2xl p-3 max-h-56 overflow-y-auto space-y-2 shadow-inner">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                Available Offers
              </p>
              {availableCoupons.map((c) => (
                <div
                  key={c.id || c.code}
                  className="p-2.5 rounded-xl border border-dashed border-gray-300 hover:border-[#0D6D6F] bg-gray-50/50 flex items-center justify-between gap-3 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-black text-[#0D6D6F] uppercase bg-[#EAF5F2] px-2 py-0.5 rounded">
                        {c.code}
                      </span>
                      {c.isSingleUse && (
                        <span className="text-[10px] text-gray-500 font-medium">
                          Single use
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-bold text-slate-800 mt-1 truncate">
                      {c.title}
                    </p>
                    <p className="text-[11px] text-gray-500 leading-tight">
                      {c.description}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => validateAndApplyCoupon(c.code)}
                    className="shrink-0 text-xs font-bold text-white bg-[#0D6D6F] hover:bg-[#0B5C58] px-3 py-1.5 rounded-lg transition-colors shadow-2xs"
                  >
                    Apply
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Credit Summary Card - RIGHT ABOVE PAY BUTTON */}
      <div className="mb-4 bg-slate-50 border border-slate-200/80 rounded-2xl p-3.5 transition-all">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[#0D6D6F] shadow-2xs">
              <CreditCard size={16} />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium leading-none">
                Amount to be credited
              </p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-base sm:text-lg font-black text-slate-900">
                  ₹{(totalCredited ?? amount).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowBillSummary(!showBillSummary)}
            className="text-xs font-semibold text-[#0D6D6F] hover:underline flex items-center gap-0.5"
          >
            {showBillSummary ? 'Hide Summary' : 'View Bill Summary'}
            {showBillSummary ? (
              <ChevronUp size={14} />
            ) : (
              <ChevronDown size={14} />
            )}
          </button>
        </div>

        {/* Collapsible Bill Summary Breakdown */}
        {showBillSummary && (
          <div className="mt-3 pt-3 border-t border-slate-200/80 text-xs space-y-1.5 animate-in fade-in duration-200">
            <div className="flex justify-between text-gray-600">
              <span>Recharge Amount</span>
              <span className="font-semibold text-slate-800">
                ₹{amount.toLocaleString('en-IN')}
              </span>
            </div>

            {appliedCoupon && (
              <div className="flex justify-between text-emerald-600 font-medium">
                <span className="flex items-center gap-1">
                  Coupon Bonus ({appliedCoupon.couponCode})
                </span>
                <span className="font-bold">
                  +₹{appliedCoupon.bonusAmount?.toLocaleString('en-IN')}
                </span>
              </div>
            )}

            <div className="flex justify-between text-slate-900 font-bold pt-1 border-t border-slate-200/60">
              <span>Total Wallet Credit</span>
              <span className="text-sm text-[#0D6D6F]">
                ₹{(totalCredited ?? amount).toLocaleString('en-IN')}
              </span>
            </div>

            <div className="flex justify-between text-slate-900 font-extrabold pt-1">
              <span>Net Payable Amount</span>
              <span className="text-sm">₹{amount.toLocaleString('en-IN')}</span>
            </div>
          </div>
        )}
      </div>

      {/* Pay CTA Button */}
      <button
        type="button"
        onClick={handlePay}
        disabled={isProcessing || amount < minAmount}
        className="w-full bg-[#0D6D6F] hover:bg-[#0B5C58] disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3.5 px-4 rounded-2xl font-black text-base shadow-lg shadow-[#0D6D6F]/20 transition-all hover:scale-[0.99] active:scale-[0.97] flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Processing Payment...</span>
          </>
        ) : (
          <>
            <span>Pay ₹{amount.toLocaleString('en-IN')}</span>
            <ArrowRight size={18} />
          </>
        )}
      </button>

      {/* Trust & Guarantee note */}
      <p className="text-[12px] flex flex-row items-center gap-1 justify-center text-gray-400 text-center mt-3 font-medium">
        <LockClosed16Filled className="text-teal" />
        100% Secure & Instant Wallet Credit
      </p>
    </div>
  );
};

export default RechargeWallet;
